import type { FlagReason, FlagReasonId, FlaggedSuspendedRow } from './types';

/** One Suspended Item: DIV, branch, and ITEM#. */
export function suspendedRowKey(row: FlaggedSuspendedRow): string {
  return [row.div, row.branch, row.itemNumber].join('\0');
}

/** PO / order numbers a reason chip spells out before eliding the rest. */
const LISTED_NUMBERS = 3;

function numberList(numbers: readonly string[]): string {
  const listed = numbers.slice(0, LISTED_NUMBERS).join(', ');
  return numbers.length > LISTED_NUMBERS ? `${listed}, …` : listed;
}

interface Fold {
  first: FlaggedSuspendedRow;
  rowCount: number;
  reasons: Map<FlagReasonId, FlagReason>;
  poNumbers: Set<string>;
  orderNumbers: Set<string>;
}

/**
 * Folds flagged rows into one per item as they arrive, so a file that
 * repeats an item millions of times holds one entry for it, not millions.
 * The first row supplies the cells; reasons, POs, and orders accumulate.
 */
export class FlaggedSuspendedItems {
  private readonly folds = new Map<string, Fold>();

  add(row: FlaggedSuspendedRow): void {
    const key = suspendedRowKey(row);
    let fold = this.folds.get(key);
    if (!fold) {
      fold = {
        first: row,
        rowCount: 0,
        reasons: new Map(),
        poNumbers: new Set(),
        orderNumbers: new Set(),
      };
      this.folds.set(key, fold);
    }
    fold.rowCount += row.rowCount;
    for (const reason of row.reasons) {
      if (!fold.reasons.has(reason.id)) fold.reasons.set(reason.id, reason);
    }
    for (const po of row.poNumbers) fold.poNumbers.add(po);
    for (const order of row.orderNumbers) fold.orderNumbers.add(order);
  }

  /** The items, in the order their first flagged row appeared. */
  rows(): FlaggedSuspendedRow[] {
    return [...this.folds.values()].map((fold) => {
      const poNumbers = [...fold.poNumbers];
      const orderNumbers = [...fold.orderNumbers];
      const reasons = [...fold.reasons.values()].map((reason) => {
        if (reason.id === 'poNumber') {
          return { ...reason, value: numberList(poNumbers) };
        }
        if (reason.id === 'orderNumber') {
          return { ...reason, value: numberList(orderNumbers) };
        }
        return reason;
      });
      return {
        ...fold.first,
        rowCount: fold.rowCount,
        poNumbers,
        orderNumbers,
        reasons,
      };
    });
  }
}
